import { AxiosRequestConfig } from "axios";

const COOKIE_NAME = "XSRF-TOKEN";
const COOKIE_PREFIX = `${COOKIE_NAME}=`;

// RFC 9110 §9.2.1 — safe (read-only) methods are exempt from CSRF.
const SAFE_METHODS = new Set(["get", "head", "options", "trace"]);

const API_BASE_URL = process.env.REACT_APP_API_NEW_URL || "";

// Cookie value may contain '=' (e.g. base64 padding), so we slice off the
// fixed-length name+separator instead of using split("=") which would
// truncate after the first '='.
export const readCsrfTokenFromCookie = (): string => {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(COOKIE_PREFIX));
  if (!match) return "";
  return decodeURIComponent(match.substring(COOKIE_PREFIX.length));
};

// Only inject CSRF token on requests routed to our API. Axios lets call sites
// pass an absolute URL that overrides baseURL; comparing origins (not string
// prefixes) prevents leaking the token to e.g. `api.appodeal.com.evil.com`.
export const isApiRequest = (url: string | undefined): boolean => {
  if (!url) return true;
  if (!/^https?:\/\//i.test(url)) return true;
  if (API_BASE_URL === "") return false;
  try {
    return new URL(url).origin === new URL(API_BASE_URL).origin;
  } catch {
    return false;
  }
};

// Axios xsrfCookieName/xsrfHeaderName only auto-injects on same-origin requests.
// Webapp + API live on separate origins (mds.appodeal.com -> neo-mw-backend.appodeal.com),
// so we must read the cookie and set the header ourselves.
export const requestCsrfInterceptor = (config: AxiosRequestConfig): AxiosRequestConfig => {
  const method = (config.method || "get").toLowerCase();
  if (SAFE_METHODS.has(method)) return config;
  if (!isApiRequest(config.url)) return config;
  const token = readCsrfTokenFromCookie();
  if (!token) return config;
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      "X-CSRF-Token": token,
    },
  };
};
